import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ProLink from '../../shared/ProLink/ProLink';
import NavigationLogo from './partials/NavigationLogo/NavigationLogo';
import { currentLocale, dataLayer, miniGazelle, shouldUseGrid } from '../../../helpers';
import './Navigation.scss';
import NavigationMobile from './NavigationMobile';
import Hamburger from './Hamburger/Hamburger';
import LoginRegister from '../../shared/LoginRegister/LoginRegister';
import classNames from 'classnames';
import HeaderSearch from '../../shared/HeaderSearch/HeaderSearch';
import Sticky from 'react-stickynode';

const INACTIVE_TEXT = 'tw-text-[#ACACAC]';
const ACTIVE_TEXT = 'tw-text-[rgba(129,220,247,1)] active-header-nav';
const ACTIVE_BG = 'tw-bg-[#151b21]';

const ITEM_BASE_CLS =
    'tw-w-full tw-flex tw-justify-between tw-items-center tw-py-[10px] ' +
    'tw-border-b tw-border-dashed tw-border-white/20 ' +
    'tw-transition-colors tw-duration-300 tw-cursor-pointer last:tw-border-b-0';

const LINK_BASE_CLS =
    'mega-menu-col2-link tw-w-full tw-flex tw-justify-between tw-items-center ' +
    'tw-px-[5px] tw-py-[1.5px] tw-border-b tw-border-dashed tw-border-white/20 ' +
    'tw-transition-colors tw-duration-300 tw-cursor-pointer last:tw-border-b-0';

const CHEVRON = (
    <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

const CLOSE_ICON = (
    <svg
        className="mega-menu-close-btn__icon group-focus-visible:tw-stroke-[rgba(129,220,247,1)]"
        fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

function makeHoverHandlers(hoverClass) {
    return {
        onMouseEnter: e => {
            e.currentTarget.classList.add(hoverClass);
            e.currentTarget.querySelector('img')?.classList.add('mega-menu-icon--hovered');
        },
        onMouseLeave: e => {
            e.currentTarget.classList.remove(hoverClass);
            e.currentTarget.querySelector('img')?.classList.remove('mega-menu-icon--hovered');
        }
    };
}

const COL1_HOVER = makeHoverHandlers('mega-menu-col1-item--hovered');
const COL2_HOVER = makeHoverHandlers('mega-menu-col2-item--hovered');

function activeTextCls(isActive) {
    return { [INACTIVE_TEXT]: !isActive, [ACTIVE_TEXT]: isActive };
}


function Col1Item({ menuItem, isActive, onClick, onFocus, onKeyDown }) {
    return (
        <button
            role="menuitem"
            data-active={String(isActive)}
            aria-haspopup={menuItem?.menuItems?.length > 0 ? 'true' : undefined}
            aria-expanded={isActive || undefined}
            onClick={onClick}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            {...COL1_HOVER}
            className={classNames(ITEM_BASE_CLS, activeTextCls(isActive))}
        >
            <div className={classNames(
                'tw-w-full tw-flex tw-justify-between tw-items-center tw-p-[10px] tw-text-[1.125rem] hover:tw-text-[rgba(129,220,247,1)] mega-menu-wrapper',
                { ...activeTextCls(isActive), [ACTIVE_BG]: isActive }
            )}>
                <div className="tw-flex tw-gap-3 tw-items-center">
                    <div className="mega-menu-icon-wrapper">
                        <img
                            className={classNames(
                                "mega-menu-icon",
                                {
                                    'mega-menu-icon--hovered': isActive,
                                    'mega-menu-icon--active': isActive,
                                }
                            )}
                            alt=""
                            role="presentation"
                            src={menuItem.iconImage?.svg ?? menuItem.iconImage?.png}
                            loading="lazy"
                            fetchPriority="high"
                            decoding="sync"
                        />
                    </div>
                    <span className='nav-title'>{menuItem?.name}</span>
                </div>
                {CHEVRON}
            </div>
        </button>
    );
}

function Col2Item({ menuItem, isActive, isHeader, onClick, onFocus, onKeyDown, onCustomEvent }) {
    const sharedProps = {
        role: 'menuitem',
        'data-active': String(isActive),
        onFocus,
        onKeyDown,
    };

    if (menuItem?.menuItems?.length > 0) {
        return (
            <button
                {...sharedProps}
                aria-haspopup="true"
                aria-expanded={isActive || undefined}
                {...COL2_HOVER}
                className={classNames(
                    ITEM_BASE_CLS,
                    'hover:tw-text-[rgba(129,220,247,1)] tw-text-[1rem] focus:tw-text-[rgba(129,220,247,1)]',
                    activeTextCls(isActive)
                )}
                onClick={onClick}
            >
                <div className={classNames(
                    'tw-w-full tw-flex tw-justify-between tw-items-center tw-p-[10px] mega-menu-wrapper',
                    { [ACTIVE_BG]: isActive }
                )}>
                    <div className="tw-flex tw-gap-5 tw-items-center">
                        <span className='nav-title'>{menuItem?.name}</span>
                    </div>
                    {CHEVRON}
                </div>
            </button>
        );
    }

    return (
        <ProLink
            {...sharedProps}
            to={menuItem.link}
            className={classNames(
                LINK_BASE_CLS,
                {
                    'header-nav': isHeader,
                    'mega-menu-wrapper': !isHeader,
                },
                activeTextCls(isActive)
            )}
            style={{ textDecoration: 'none' }}
            onClick={() => {
                onCustomEvent()
                onClick()
            }}
        >
            <span
                className={classNames(
                    {
                        'mega-menu-col2-link__label': !isHeader,
                    },
                    'nav-title'
                )}
            >
                {menuItem?.name}
            </span>
        </ProLink>
    );
}

function Col3Item({ menuItem, isActive, isHeader, onClick, onFocus, onKeyDown }) {
    return (
        <ProLink
            role="menuitem"
            data-active={String(isActive)}
            onClick={onClick}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            to={menuItem.link}
            className={classNames(
                LINK_BASE_CLS,
                {
                    'header-nav': isHeader,
                    'mega-menu-wrapper': !isHeader,
                },
                activeTextCls(isActive)
            )}
            style={{ textDecoration: 'none' }}
        >
            <span
                className={classNames(
                    {
                        'mega-menu-col2-link__label': !isHeader,
                    },
                    'nav-title'
                )}
            >
                {menuItem?.name}
            </span>
        </ProLink>
    );
}

export default function Navigation({
    data: {
        structure: { nav },
        user,
    },
}) {
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [showSelectedMenu, setShowSelectedMenu] = useState(false);
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
    const [showLoginRegister, setShowLoginRegister] = useState(false);
    const [showSearchAhead, setShowSearchAhead] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);

    const navigationRef = useRef(null);
    const headerSearchRef = useRef(null);
    const headerSearchOpenRef = useRef(null);
    const dropdownRef = useRef(null);
    const closeBtnRef = useRef(null);
    const isKeyboardOpenRef = useRef(false);
    const viewAllRef = useRef(null);
    const selectedMenuRef = useRef(null);

    const searchInputID = 'navigation-typeahead';

    useEffect(() => {
        if (showSearchAhead) {
            setTimeout(() => {
                headerSearchRef.current?.querySelector(`#${searchInputID}`)?.focus();
            }, 100);
        }
    }, [showSearchAhead]);

    useEffect(() => {
        if (!showSelectedMenu) return;
        if (document.activeElement) document.activeElement.blur();
        if (isKeyboardOpenRef.current) {
            requestAnimationFrame(() => {
                if (!dropdownRef.current) return;
                const firstFocusable =
                    dropdownRef.current.querySelector('[data-col="1"] button') ??
                    dropdownRef.current.querySelector('a, button');
                firstFocusable?.focus();
            });
        }
    }, [selectedMenu, showSelectedMenu]);

    const loginRegisterClassNames = classNames({
        container: true,
        'login-register-tooltip': true,
        active: showLoginRegister,
    });

    const searchAheadClassNames = classNames({
        container: true,
        'search-tooltip': true,
        'navbar-right': true,
        active: showSearchAhead,
    });

    const openNavItem = (e, navItem) => {
        e.preventDefault();
        if (selectedMenuRef.current?.id === navItem.id && showSelectedMenu) {
            closeNavMenu();
            return;
        }
        closeNavMenu();
        selectedMenuRef.current = navItem;
        setSelectedMenu(navItem);
        setActiveMenu(null);
        const parentListItem = e.currentTarget.closest('.navigation-top-level-item');
        parentListItem?.classList.add('navigation-open');
        parentListItem?.classList.remove('navigation-hidden');
        setShowSelectedMenu(true);
    };

    const closeNavMenu = (restoreFocus = true) => {
        const prevSelectedId = selectedMenuRef.current?.id;
        selectedMenuRef.current = null;

        setShowSelectedMenu(false);
        setSelectedMenu(null);
        setActiveMenu(null);

        navigationRef.current
            ?.querySelectorAll('.navigation-top-level-item')
            .forEach(item => {
                item.classList.remove('navigation-open');
                item.classList.add('navigation-hidden');
            });

        if (restoreFocus && prevSelectedId) {
            navigationRef.current
                ?.querySelector(`#${prevSelectedId} > button`)
                ?.focus({ preventScroll: true });
        }
    };


    useEffect(() => {
        if (!showSelectedMenu) return;

        const handleClickOutside = (event) => {
            const dropdownEl = dropdownRef.current;

            if (!dropdownEl) return;

            if (dropdownEl.contains(event.target)) return;

            const triggerBtn = navigationRef.current?.querySelector(`#${selectedMenuRef.current?.id} > button`);
            if (triggerBtn?.contains(event.target)) return;

            closeNavMenu(false);
        };

        document.addEventListener("pointerdown", handleClickOutside, true);

        return () => {
            document.removeEventListener("pointerdown", handleClickOutside, true);
        };
    }, [showSelectedMenu]);

    useEffect(() => {
        if (!showSelectedMenu) return;

        const handleEsc = (e) => {
            if (e.key === "Escape") {
                closeNavMenu(false);
            }
        };

        document.addEventListener("keydown", handleEsc);

        return () => {
            document.removeEventListener("keydown", handleEsc);
        };
    }, [showSelectedMenu, closeNavMenu]);

    const closeSearchAhead = () => setShowSearchAhead(false);

    const handleSelectedMenu = (menu, subMenu = null, childMenu = null) => {
        setActiveMenu({ menu, subMenu, childMenu });
    };

    const handleItemClick = (e, menuItemChildName, subItemName, menuItemLink, isViewAllLink) => {
        const formattedMenuItemChildName = menuItemChildName.toLowerCase().replace(/\s+/g, '-');
        const formattedSubItemName = subItemName.toLowerCase().replace(/\s+/g, '-');
        const typeMap = {
            'professional-products': miniGazelle.label('products'),
            solutions: miniGazelle.label('solutions'),
            'where-to-buy': miniGazelle.label('wheretobuy'),
            support: miniGazelle.label('support'),
            'contact-us': miniGazelle.label('contactus'),
            promotions: miniGazelle.label('promotions'),
            'view-all': miniGazelle.label('viewAll'),
        };
        dataLayer.triggerEvent('header_click', {
            detail: {
                type: !isViewAllLink ? (typeMap[formattedMenuItemChildName] || formattedMenuItemChildName) : 'view-all',
                name: formattedSubItemName,
                link: menuItemLink,
            },
        });
    };

    const handleKeyDown = e => {
        if (e.key === 'Escape') {
            closeNavMenu();
            closeSearchAhead();
        }
    };

    const handleSearchKeyDown = e => {
        if (e.key === 'Escape') {
            closeSearchAhead();
            headerSearchOpenRef.current?.focus();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedMenu, showSelectedMenu]);

    useEffect(() => {
        const srcs = new Set();
        const collect = (items) => {
            items?.forEach(item => {
                const src = item.iconImage?.svg || item.iconImage?.png;
                if (src) srcs.add(src);
                collect(item.menuItems);
            });
        };
        nav.forEach(navItem => collect(navItem.menuItems));

        const links = [];
        srcs.forEach(src => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = src.endsWith('.svg') ? 'image' : 'image';
            link.href = src;
            document.head.appendChild(link);
            links.push(link);
        });
        return () => links.forEach(l => l.remove());
    }, []);

    const colSelector = (col) => `[data-col="${col}"] button, [data-col="${col}"] a`;

    const handleCol1KeyDown = (e, menuItem) => {
        const col1Items = Array.from(dropdownRef.current?.querySelectorAll('[data-col="1"] button') || []);
        const currentIndex = col1Items.findIndex(el => el === document.activeElement);

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const firstSubMenu = menuItem?.menuItems?.[0];
            if (firstSubMenu) {
                handleSelectedMenu(menuItem, firstSubMenu, firstSubMenu?.menuItems?.[0]);
                setTimeout(() => dropdownRef.current?.querySelector(colSelector(2))?.focus(), 0);
            }
        }

        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();

            const nextItem = col1Items[currentIndex + 1];
            if (nextItem) {
                nextItem.focus();
                return;
            }
            if (selectedMenu?.navigationShowViewAll) {
                viewAllRef.current?.querySelector('a, button')?.focus();
            } else {
                closeBtnRef.current?.focus();
            }
        }

        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            if (currentIndex === 0) {
                navigationRef.current?.querySelector(`#${selectedMenu?.id} > button`)?.focus();
            } else {
                col1Items[currentIndex - 1]?.focus();
            }
        }

        if (e.key === 'Escape') closeNavMenu();
    };

    const handleCol2KeyDown = (e, menuItem) => {
        const col2Items = Array.from(
            dropdownRef.current?.querySelectorAll(colSelector(2)) || []
        );

        const currentIndex = col2Items.findIndex(
            el => el === document.activeElement
        );

        const hasChildren = menuItem?.menuItems?.length > 0;

        if (e.key === 'Enter' || e.key === ' ') {
            if (!hasChildren) return;

            e.preventDefault();

            handleSelectedMenu(
                activeMenu?.menu,
                menuItem,
                menuItem.menuItems?.[0]
            );

            setTimeout(() => {
                const firstCol3 =
                    dropdownRef.current?.querySelector(
                        '[data-col="3"] a, [data-col="3"] button'
                    );

                if (firstCol3) {
                    firstCol3.focus();
                } else {
                    if (selectedMenu?.navigationShowViewAll) {
                        viewAllRef.current?.querySelector('a, button')?.focus();
                    } else {
                        closeBtnRef.current?.focus();
                    }
                }
            }, 0);

            return;
        }

        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();

            const nextItem = col2Items[currentIndex + 1];

            if (nextItem) {
                nextItem.focus();
                return;
            }

            const col1Array = selectedMenu?.menuItems || [];
            const col2Array = activeMenu?.menu?.menuItems || [];

            const activeCol1Index = col1Array.findIndex(
                it => it?.name === activeMenu?.menu?.name
            );

            const isLastCol1 = activeCol1Index === col1Array.length - 1;
            const isLastCol2 = currentIndex === col2Array.length - 1;

            if (isLastCol1 && isLastCol2) {
                const firstCol3 =
                    dropdownRef.current?.querySelector(
                        '[data-col="3"] a, [data-col="3"] button'
                    );

                if (firstCol3) {
                    firstCol3.focus();
                } else {
                    if (selectedMenu?.navigationShowViewAll) {
                        viewAllRef.current?.querySelector('a, button')?.focus();
                    } else {
                        closeBtnRef.current?.focus();
                    }
                }
                return;
            }

            const nextCol1 = col1Array[activeCol1Index + 1];

            if (nextCol1?.menuItems?.length > 0) {
                const firstSubMenu = nextCol1.menuItems[0];

                handleSelectedMenu(
                    nextCol1,
                    firstSubMenu,
                    firstSubMenu.menuItems?.[0]
                );

                setTimeout(() => {
                    dropdownRef.current
                        ?.querySelector('[data-col="2"] button, [data-col="2"] a')
                        ?.focus();
                }, 0);

                return;
            }

            if (selectedMenu?.navigationShowViewAll) {
                viewAllRef.current?.querySelector('a, button')?.focus();
            } else {
                closeBtnRef.current?.focus();
            }
            return;
        }

        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();

            if (currentIndex === 0) {
                dropdownRef.current
                    ?.querySelector('[data-col="1"] button[data-active="true"]')
                    ?.focus();
            } else {
                col2Items[currentIndex - 1]?.focus();
            }

            return;
        }
        if (e.key === 'Escape') {
            closeNavMenu();
        }
    };

    const handleCol3KeyDown = (e, isLastItem) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            const col1 = selectedMenu?.menuItems || [];
            const activeCol1Index = col1.findIndex(it => it?.name === activeMenu?.menu?.name);
            const isLastCol1 = activeCol1Index === col1.length - 1;
            const col2 = activeMenu?.menu?.menuItems || [];
            const activeCol2Index = col2.findIndex(it => it?.name === activeMenu?.subMenu?.name);
            const isLastCol2 = activeCol2Index === col2.length - 1;

            if (isLastCol1 && isLastCol2 && isLastItem) {
                e.preventDefault();
                if (selectedMenu?.navigationShowViewAll) {
                    viewAllRef.current?.querySelector('a, button')?.focus();
                } else {
                    closeBtnRef.current?.focus();
                }
                return;
            }
            if (isLastItem) {
                e.preventDefault();
                focusNextCol2Item();
            }
        }

        if (e.key === 'Tab' && e.shiftKey) {
            const col3Items = dropdownRef.current?.querySelectorAll(colSelector(3)) || [];
            if (col3Items[0] === document.activeElement) {
                e.preventDefault();
                dropdownRef.current?.querySelector('[data-col="2"] button[data-active="true"]')?.focus();
            }
        }

        if (e.key === 'Escape') closeNavMenu();
    };

    const handleViewAllKeyDown = (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            closeBtnRef.current?.focus();
        }

        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            const col3Items = dropdownRef.current?.querySelectorAll(colSelector(3));
            col3Items?.[col3Items.length - 1]?.focus();
        }
        if (e.key === 'Escape') closeNavMenu();
    };

    const handleCloseBtnKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            closeNavMenu();
            return;
        }

        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            closeNavMenu(false);
            const navItems = Array.from(navigationRef.current?.querySelectorAll('.navigation-top-level-item'));
            const currentNavItem = navigationRef.current?.querySelector(`#${selectedMenu?.id}`);
            const currentIndex = navItems.findIndex(item => item === currentNavItem);
            navItems[currentIndex + 1]?.querySelector('button')?.focus();
        }

        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            const col3Items = dropdownRef.current?.querySelectorAll(colSelector(3));
            col3Items?.[col3Items.length - 1]?.focus();
        }

        if (e.key === 'Escape') closeNavMenu();
    };

    const focusNextCol2Item = () => {
        const col1Array = selectedMenu?.menuItems || [];
        const col2Array = activeMenu?.menu?.menuItems || [];
        const activeCol1Index = col1Array.findIndex(it => it?.name === activeMenu?.menu?.name);
        const activeCol2Index = col2Array.findIndex(it => it?.name === activeMenu?.subMenu?.name);
        const nextCol2 = col2Array[activeCol2Index + 1];

        if (nextCol2) {
            handleSelectedMenu(activeMenu?.menu, nextCol2, nextCol2.menuItems?.[0]);
            setTimeout(() => dropdownRef.current?.querySelector(colSelector(3))?.focus(), 0);
        } else {
            const nextCol1 = col1Array[activeCol1Index + 1] || col1Array[0];
            if (nextCol1?.menuItems?.length > 0) {
                const firstSubMenu = nextCol1.menuItems[0];
                handleSelectedMenu(nextCol1, firstSubMenu, firstSubMenu.menuItems?.[0]);
                setTimeout(() => dropdownRef.current?.querySelector(colSelector(3))?.focus(), 0);
            }
        }
    };

    const renderMegaMenu = (navItem) => {
        const hasCol3 = activeMenu?.menu?.menuItems
            ?.flatMap(ele => ele.menuItems ?? [])?.length > 0;

        return (
            <>
                <div
                    ref={dropdownRef}
                    className="navigation-drop"
                    data-testid={`${navItem.name}-navigation-drop`}
                    role="menu"
                    aria-label={`${navItem.name} submenu`}
                    tabIndex={-1}
                >
                    <div className="mega-menu-nav">
                        <div className='tw-flex tw-justify-end'>
                            <div className={classNames(
                                'mega-menu-container tw-w-[100%]',
                                {
                                    'tw-justify-between': hasCol3,
                                    'tw-gap-[4.2rem]': !hasCol3
                                }
                            )}>
                                <div data-col="1" role="group" aria-label="Categories" className="tw-w-[30%]">
                                    {selectedMenu?.menuItems?.map((menuItem, ii) => (
                                        <Col1Item
                                            key={ii}
                                            menuItem={menuItem}
                                            isActive={activeMenu?.menu?.name === menuItem?.name}
                                            onClick={() => handleSelectedMenu(menuItem, null, null)}
                                            onFocus={() => handleSelectedMenu(menuItem, null, null)}
                                            onKeyDown={e => handleCol1KeyDown(e, menuItem)}
                                        />
                                    ))}
                                </div>

                                <div data-col="2" role="group" aria-label="Sub categories" className="tw-w-[30%] tw-flex tw-flex-col">
                                    {activeMenu?.menu?.menuItems?.map((menuItem, ii) => {
                                        const isLastItem = ii === (activeMenu?.menu?.menuItems?.length ?? 0) - 1;
                                        return (
                                            <Col2Item
                                                key={ii}
                                                menuItem={menuItem}
                                                isHeader={ii === 0}
                                                isActive={activeMenu?.subMenu?.name === menuItem?.name}
                                                onClick={(e) => {
                                                    handleSelectedMenu(activeMenu?.menu, menuItem) 
                                                }}
                                                onCustomEvent={(e) => {
                                                    handleItemClick(
                                                        e,
                                                        navItem.name,
                                                        menuItem.name,
                                                        menuItem.link,
                                                    )
                                                }}
                                                onFocus={() => handleSelectedMenu(activeMenu?.menu, menuItem)}
                                                onKeyDown={e => handleCol2KeyDown(e, menuItem, isLastItem)}
                                            />
                                        );
                                    })}
                                </div>

                                {hasCol3 && (
                                    <div data-col="3" role="group" aria-label="Child categories" className="tw-w-[30%]">
                                        {activeMenu?.subMenu?.menuItems?.map((menuItem, ii) => {
                                            const isLastItem = ii === (activeMenu?.subMenu?.menuItems?.length ?? 0) - 1;
                                            return (
                                                <Col3Item
                                                    key={ii}
                                                    menuItem={menuItem}
                                                    isHeader={ii === 0}
                                                    isActive={activeMenu?.childMenu?.name === menuItem?.name}
                                                    onClick={(e) => {
                                                        handleSelectedMenu(activeMenu?.menu, activeMenu?.subMenu, menuItem)
                                                        handleItemClick(
                                                            e,
                                                            navItem.name,
                                                            menuItem.name,
                                                            menuItem.link,
                                                        )
                                                    }}
                                                    onFocus={() => handleSelectedMenu(activeMenu?.menu, activeMenu?.subMenu, menuItem)}
                                                    onKeyDown={e => handleCol3KeyDown(e, isLastItem)}
                                                />
                                            );
                                        })}
                                    </div>
                                )}

                            </div>
                            <div className='tw-flex tw-justify-end tw-h-fit'>
                                <div
                                    ref={closeBtnRef}
                                    tabIndex={0}
                                    role="button"
                                    aria-label="Close menu"
                                    onKeyDown={handleCloseBtnKeyDown}
                                    onClickCapture={closeNavMenu}
                                    className="mega-menu-close-btn group tw-transition-colors tw-duration-300 tw-cursor-pointer hover:[&>svg]:tw-stroke-[rgba(129,220,247,1)]"
                                >
                                    {CLOSE_ICON}
                                </div>
                            </div>
                        </div>
                        {selectedMenu && selectedMenu.navigationShowViewAll && (
                            <div className="tw-pt-10 tw-pb-[1.5rem]" ref={viewAllRef}>
                                <hr className="tw-border-[#ACACAC] tw-border-1 tw-mb-5" />
                                <p className='tw-px-[10px]'>
                                    <ProLink
                                        onKeyDown={handleViewAllKeyDown}
                                        onClick={e =>
                                            handleItemClick(
                                                e,
                                                navItem.name,
                                                selectedMenu.name,
                                                selectedMenu.link,
                                                selectedMenu.navigationShowViewAll
                                            )
                                        }
                                        to={selectedMenu.link}
                                        className="viewall-tab"
                                    >
                                        {miniGazelle.label('viewAll')}
                                    </ProLink>
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    };

    const caseStudyMenu = (navItem, ref) => {
        const renderMenuRow = (item) => (
            <ProLink
                key={item.id}
                to={item.link}
                role="menuitem"
                className="case-study-menu-row mega-menu-wrapper"
                onClick={(e) => {
                    handleItemClick(
                        e,
                        navItem.name,
                        item.name,
                        item.link,
                    )
                }}
            >
                {(item.iconImage?.svg || item.iconImage?.png) && (
                    <img
                        src={item.iconImage.svg || item.iconImage.png}
                        alt="" role="presentation" width="32" height="32"
                        className="case-study-menu-row__icon"
                        loading="lazy"
                        fetchPriority="high"
                        decoding="sync"
                    />
                )}
                <span>{item.name}</span>
            </ProLink>
        );

        return (
            <div
                ref={ref}
                className="new-navigation-drop"
                data-testid={`${navItem.name}-navigation-drop`}
                role="menu"
                aria-label={`${navItem.name} submenu`}
                tabIndex={-1}
            >
                {navItem.menuItems?.map((ele) => (
                    <div key={ele.name} className="case-study-section">
                        <div className="case-study-section-heading">
                            {miniGazelle.label(ele.name, true)}
                        </div>
                        {ele.menuItems.map(item => renderMenuRow(item))}
                    </div>
                ))}
            </div>
        );
    };

    const renderDropdownMenu = (navItem, ref) => (
        <div
            ref={ref}
            className={`new-navigation-drop ${navItem.link.split('/').filter(Boolean).pop()}-nav-container`}
            data-testid={`${navItem.name}-navigation-drop`}
            role="menu"
            aria-label={`${navItem.name} submenu`}
            tabIndex={-1}
        >
            <div className="dropdown-menu-inner">
                <div data-col="1" role="group" aria-label="Categories" className="tw-flex tw-flex-col tw-w-full">
                    {navItem.menuItems?.map((item) => (
                        <ProLink
                            key={item.id}
                            to={item.link}
                            role="menuitem"
                            className="case-study-menu-row mega-menu-wrapper"
                            onClick={(e) => {
                                handleItemClick(
                                    e,
                                    navItem.name,
                                    item.name,
                                    item.link,
                                )
                            }
                            }
                            onMouseEnter={e => {
                                e.currentTarget.classList.add('case-study-menu-row--hovered');
                                e.currentTarget.querySelector('img')?.classList.add('mega-menu-icon--hovered');
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.classList.remove('case-study-menu-row--hovered');
                                e.currentTarget.querySelector('img')?.classList.remove('mega-menu-icon--hovered');
                            }}
                        >
                            {(item.iconImage?.svg || item.iconImage?.png) && (
                                <img
                                    src={item.iconImage.svg || item.iconImage.png}
                                    alt="" role="presentation" width="32" height="32"
                                    className="dropdown-menu-item__icon"
                                    loading="lazy"
                                    fetchPriority="high"
                                    decoding="sync"
                                />
                            )}
                            <span>{item.name}</span>
                        </ProLink>
                    ))}
                </div>
            </div>
        </div>
    );

    const handleRenderDropdown = (navItem, ref) => {
        if (navItem?.link?.endsWith('/casestudies')) return caseStudyMenu(navItem, ref);
        const hasChild = navItem?.menuItems.reduce((acc, item) => acc + (item?.menuItems?.length || 0), 0);
        return hasChild ? renderMegaMenu(navItem) : renderDropdownMenu(navItem, ref);
    };

    return (
        <Sticky enabled={true} innerZ={500}>
            <header id="navigation" ref={navigationRef} data-testid="navigation">
                {showSearchAhead && (
                    <button
                        className="dropdown-bg"
                        aria-label="Close search"
                        onClick={() => setShowSearchAhead(false)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setShowSearchAhead(false); }}
                    />
                )}

                {mobileMenuVisible && (
                    <NavigationMobile
                        nav={{ topLevel: true, menuItems: nav }}
                        setVisible={setMobileMenuVisible}
                        visible={mobileMenuVisible}
                    />
                )}

                <div>
                    <div
                        data-testid="navigation-bar"
                        className="navigation-bar tw-bg-black tw-flex tw-items-center tw-w-full tw-h-[64px]"
                    >
                        <div className="navigation-container tw-w-full tw-px-4">
                            <div className="tw-flex tw-justify-between tw-text-white tw-font-sst-regular tw-relative navigation-wrapper">

                                <div className="tw-flex tw-items-center">
                                    <NavigationLogo />
                                </div>

                                <div className="tw-flex tw-justify-between">
                                    <nav
                                        id="desktop-navigation"
                                        className="tw-px-10 tw-hidden md:tw-block"
                                        aria-label="Main navigation"
                                        data-testid="open-menu"
                                    >
                                        <ul className="tw-flex" role="menubar">
                                            {nav.map((navItem, i) => {
                                                const isOpen = selectedMenu?.id === navItem.id && showSelectedMenu;
                                                const isMegaMenu = !navItem?.link?.endsWith('/casestudies') && navItem?.menuItems?.reduce((acc, item) => acc + (item?.menuItems?.length || 0), 0) > 0;
                                                return (
                                                    <li
                                                        id={navItem.id}
                                                        key={i}
                                                        role="none"
                                                        className="navigation-top-level-item navigation-hidden tw-mx-[0.05rem] tw-flex tw-items-center tw-transition-all tw-h-16"
                                                        data-testid={`${navItem.name}-container`}
                                                    >
                                                        <button
                                                            role="menuitem"
                                                            aria-haspopup="true"
                                                            aria-expanded={isOpen}
                                                            data-testid={`${navItem.name}-button`}
                                                            onClick={e => {
                                                                isKeyboardOpenRef.current = false;
                                                                openNavItem(e, navItem);
                                                            }}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    isKeyboardOpenRef.current = true;
                                                                    openNavItem(e, navItem);
                                                                }
                                                            }}
                                                        >
                                                            <div className={classNames(
                                                                'nav-top-btn-inner tw-flex tw-items-center tw-gap-1 tw-py-2 tw-px-5 hover:tw-bg-[#151b21] hover:tw-text-[#76D7F6]',
                                                                {
                                                                    'nav-top-btn-inner--active tw-bg-[#151b21]': isOpen,
                                                                    'tw-text-[#76D7F6]': isOpen,
                                                                }
                                                            )}>
                                                                <span>{navItem.name}</span>
                                                                <svg
                                                                    className={classNames('nav-chevron-icon', { 'nav-chevron-icon--open': isOpen })}
                                                                    fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"
                                                                >
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 9l7 7 7-7" />
                                                                </svg>
                                                            </div>
                                                        </button>

                                                        {isMegaMenu
                                                            ? isOpen && handleRenderDropdown(navItem, dropdownRef)
                                                            : handleRenderDropdown(navItem, isOpen ? dropdownRef : null)
                                                        }
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </nav>
                                </div>

                                <div className="tw-flex tw-items-center tw-gap-3 tw-relative">
                                    {user.signedIn && (
                                        <button
                                            className="mr-3"
                                            onClick={e => {
                                                e.preventDefault();
                                                setShowLoginRegister(!showLoginRegister);
                                            }}
                                            data-testid="sign-in"
                                        >
                                            <span className="ngp-icon ngp-icon-user" data-testid="sign-out">
                                                <span className="sr-only">{miniGazelle.label('account')}</span>
                                            </span>
                                        </button>
                                    )}

                                    <button
                                        data-testid="open-search"
                                        aria-label="Open search"
                                        aria-expanded={showSearchAhead}
                                        onClick={e => {
                                            e.preventDefault();
                                            setShowSearchAhead(!showSearchAhead);
                                        }}
                                        ref={headerSearchOpenRef}
                                    >
                                        <svg
                                            fill="none" stroke="currentColor" strokeWidth="1.5"
                                            viewBox="0 0 24 24" className="nav-search-icon" aria-hidden="true"
                                        >
                                            <path
                                                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                                                strokeLinecap="round" strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>

                                    <div className="tw-flex md:tw-hidden tw-ml-5" data-testid="hamburger">
                                        <Hamburger setIsOpen={setMobileMenuVisible} isOpen={mobileMenuVisible} />
                                    </div>
                                </div>
                            </div>

                            <LoginRegister
                                loginRegisterClassNames={loginRegisterClassNames}
                                locale={currentLocale.get()}
                                user={user}
                            />

                            <div ref={headerSearchRef} data-testid="header-search">
                                <HeaderSearch
                                    searchAheadClassNames={searchAheadClassNames}
                                    locale={currentLocale.get()}
                                    searchInputID={searchInputID}
                                    onKeyDown={handleSearchKeyDown}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </Sticky>
    );
}

Navigation.propTypes = {
    data: PropTypes.shape({
        structure: PropTypes.shape({
            nav: PropTypes.array.isRequired,
        }).isRequired,
        user: PropTypes.shape({
            signedIn: PropTypes.bool,
            data: PropTypes.shape({
                name: PropTypes.string,
                email: PropTypes.string,
                updateDetails: PropTypes.string,
            }),
        }).isRequired,
    }).isRequired,
};
